import { Text } from '@backend/lib-design-system';
import { NextFilledIcon } from '@backend/lib-design-system/icons';
import Image from 'next/image';
import { type FC, Fragment } from 'react';
import { styled } from 'styled-components';

import { Container } from '~/components/Container/Container';
import { JsonLdScript } from '~/components/JsonLd/jsonLd';
import { Link } from '~/components/Link';
import { getJobsUrl } from '~/config';
import { useTranslation } from '~/hooks';
import { useHref } from '~/hooks/useHref';
import { getBreadcrumbListJsonld } from '~/utils/jsonLD';

const BreadcrumbsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BreadcrumbLink = styled(Link)`
  color: #272356 !important;
  text-decoration: none !important;

  &:hover {
    text-decoration: underline;
  }
`;

const HomeIconWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 30px;
  height: 30px;
`;

const NextIconWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 16px;
  height: 16px;
  color: #93a3c0;
`;

export type BreadcrumbItemType = {
  label: string;
  href?: string;
};

interface BreadcrumbsProps {
  items: BreadcrumbItemType[];
}

export const Breadcrumbs: FC<BreadcrumbsProps> = ({ items }) => {
  const t = useTranslation();
  const { baseHref } = useHref();

  if (!items?.length) {
    return null;
  }

  const itemsWithHome = [{ label: t('breadcrumbs.home'), href: baseHref }, ...items];
  const jsonLd = getBreadcrumbListJsonld(
    itemsWithHome.map(({ label, href }) => ({ name: label, url: href && `${getJobsUrl()}${href}` })),
  );

  return (
    <Container padding={{ bottom: 0 }}>
      <JsonLdScript id="breadcrumb-jsonld" data={jsonLd} />
      <BreadcrumbsWrapper>
        <HomeIconWrapper>
          <Image src="/images/home-icon.svg" alt="Home" width={30} height={30} />
        </HomeIconWrapper>
        {itemsWithHome.map((item, index) => (
          <Fragment key={index}>
            {index > 0 && (
              <NextIconWrapper>
                <NextFilledIcon size="s" />
              </NextIconWrapper>
            )}
            {item.href ? (
              <BreadcrumbLink href={item.href}>
                <Text variant="body">{item.label}</Text>
              </BreadcrumbLink>
            ) : (
              <Text variant="body">{item.label}</Text>
            )}
          </Fragment>
        ))}
      </BreadcrumbsWrapper>
    </Container>
  );
};
